import { useMemo, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Cpu, Download, Printer, Pencil } from 'lucide-react';
import { Button } from '@/components/ui/button.jsx';
import { toast } from '@/components/ui/toaster.jsx';
import TokenRow from '@/components/tap/TokenRow.jsx';
import EditBatchCopyDialog from '@/components/tap/EditBatchCopyDialog.jsx';
import useTapBatch from '@/hooks/useTapBatch.js';

// Sort: unprogrammed → programmed → handed-out → registered → claimed →
// revoked. Keeps the next chip to program at the top of the list.
const RANK = { minted: 0, programmed: 1, handed_out: 2, registered: 3, claimed: 4, revoked: 5 };

export default function TapBatch() {
  const { label } = useParams();
  const navigate = useNavigate();
  const { tokens, error, loading, refresh } = useTapBatch(label);
  const [editCopyOpen, setEditCopyOpen] = useState(false);

  const sorted = useMemo(() => {
    if (!tokens) return null;
    return [...tokens].sort((a, b) => (RANK[a.status] ?? 9) - (RANK[b.status] ?? 9));
  }, [tokens]);

  const nextUnprogrammed = useMemo(
    () => (tokens || []).find((t) => !t.programmed_at && t.status !== 'revoked'),
    [tokens]
  );

  function programNext() {
    if (nextUnprogrammed) navigate(`/admin/tap/program/${nextUnprogrammed.id}`);
    else toast.success('All tokens in this batch are programmed.');
  }

  function exportCsv() {
    if (!tokens?.length) return;
    const rows = [['id', 'url', 'status', 'handed_out_to'].join(',')];
    for (const t of tokens) {
      rows.push([t.id, t.url, t.status, `"${(t.handed_out_to || '').replace(/"/g, '""')}"`].join(','));
    }
    const blob = new Blob([rows.join('\n')], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `tap-${label}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success('CSV exported');
  }

  return (
    <div className="p-4">
      <div className="flex items-center gap-2">
        <Button variant="ghost" size="icon" aria-label="Back to inventory" onClick={() => navigate('/admin/tap')}>
          <ArrowLeft size={18} strokeWidth={1.5} />
        </Button>
        <div>
          <h2 className="text-lg font-semibold text-text">{label}</h2>
          <p className="text-xs text-text-muted">{tokens ? `${tokens.length} tokens` : '…'}</p>
        </div>
      </div>

      {/* Bulk actions */}
      <div className="mt-4 flex flex-wrap gap-2">
        <Button size="sm" onClick={programNext} disabled={!nextUnprogrammed}>
          <Cpu size={15} strokeWidth={1.5} /> Program next unprogrammed
        </Button>
        <Button size="sm" variant="outline" onClick={() => setEditCopyOpen(true)} disabled={!tokens?.length}>
          <Pencil size={15} strokeWidth={1.5} /> Edit copy
        </Button>
        <Button size="sm" variant="outline" onClick={exportCsv} disabled={!tokens?.length}>
          <Download size={15} strokeWidth={1.5} /> Export URLs (.csv)
        </Button>
        <Button size="sm" variant="outline" onClick={() => toast('Print labels — coming in Phase 2')}>
          <Printer size={15} strokeWidth={1.5} /> Print labels
        </Button>
      </div>

      {/* Token list */}
      <div className="mt-4 overflow-hidden rounded-card border border-border bg-surface">
        {loading && !sorted && <div className="p-6 text-sm text-text-muted">Loading…</div>}
        {error && !sorted && (
          <div className="p-4 text-sm text-accent">Couldn't load tokens: {error.code || error.message}</div>
        )}
        {sorted && sorted.length === 0 && (
          <div className="p-6 text-center text-sm text-text-muted">No tokens in this batch.</div>
        )}
        {sorted && sorted.map((t) => <TokenRow key={t.id} token={t} onMutated={refresh} />)}
      </div>

      <EditBatchCopyDialog
        open={editCopyOpen}
        onOpenChange={setEditCopyOpen}
        label={label}
        tokens={tokens}
        onSaved={refresh}
      />
    </div>
  );
}
